
import { Region } from '@/data/types/region-types';
import { useLanguage } from '@/contexts/language';
import { Progress } from '@/components/ui/progress';
import { getFundingPercentage } from '@/data/utils/region-utils';
import { formatCurrency, formatCurrencyShort } from '@/data/utils/money-utils';
import { Button } from '@/components/ui/button';
import { DollarSign, Heart } from 'lucide-react';

interface FundingProgressProps {
  region: Region;
  onPledgeClick: () => void;
}

const FundingProgress: React.FC<FundingProgressProps> = ({ region, onPledgeClick }) => {
  const { t } = useLanguage();
  
  // Calculate percentage based on pledges and target, ensuring we don't divide by 0
  const fundingPercentage = region.target_need > 0 
    ? Math.min(Math.round((region.pledges / region.target_need) * 100), 100)
    : 0;

  const getFundingColor = (percentage: number) => {
    if (percentage < 25) return 'bg-red-500';
    if (percentage < 50) return 'bg-orange-500';
    if (percentage < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="mb-4">
      <div className="flex flex-col gap-1">
        <div className="text-center font-bold text-2xl funding-amount">
          {formatCurrencyShort(region.pledges || 0)}
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">
            {t('fundingProgress')}
          </span>
          <span className="text-sm font-semibold">
            {fundingPercentage}%
          </span>
        </div>
      </div>
      <Progress 
        value={fundingPercentage} 
        className="h-2 bg-gray-100 mt-1" 
        indicatorClassName={getFundingColor(fundingPercentage)}
      />
      <div className="flex justify-between text-xs mt-1 text-gray-600">
        <span>{t('pledged')}</span>
        <span>{formatCurrencyShort(region.target_need || 0)} {t('needed')}</span>
      </div>
      
      {/* Pledge button with corrected text */}
      <div className="mt-4 space-y-2">
        <Button 
          className="w-full py-3 px-4 bg-[#1EAEDB] hover:bg-[#1795BD] text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
          onClick={onPledgeClick}
        >
          <DollarSign className="h-5 w-5" />
          {t('makePledge')}
        </Button>
      </div>
    </div>
  );
};

export default FundingProgress;
